import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Home, Mail, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { setPageMeta } from '../utils/setPageMeta';

const ThankYouPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    setPageMeta(
      'Thank You - Legal Flick',
      'Thank you for contacting Legal Flick. We have received your message and will get back to you shortly.'
    );
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Thank You Section */}
      <section className="flex-1 py-20 bg-gradient-to-br from-blue-50 via-white to-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            {/* Success Icon */}
            <div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mb-8">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>

            {/* Main Message */}
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Thank You!
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              We've received your message and appreciate you reaching out to us. Our team will review your inquiry and get back to you as soon as possible.
            </p>

            {/* What's Next Section */}
            <Card className="mb-12 text-left hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">What happens next?</h2>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                      <span className="text-blue-600 font-bold">1</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">We review your inquiry</h3>
                      <p className="text-gray-600">Our expert team will carefully review your message and determine the best way to assist you.</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                      <span className="text-blue-600 font-bold">2</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">You'll hear from us soon</h3>
                      <p className="text-gray-600">Expect a response within 24 hours during business days. Urgent matters are prioritized.</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                      <span className="text-blue-600 font-bold">3</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">We'll provide expert guidance</h3>
                      <p className="text-gray-600">Our team will provide the information or assistance you need to move forward with confidence.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => navigate('/')}
              >
                <Home className="w-5 h-5 mr-2" />
                Back to Home
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
                onClick={() => navigate('/about')}
              >
                Learn More About Us
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Additional Information */}
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              <Card className="hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <CardContent className="p-6 text-center">
                  <Mail className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                  <h3 className="font-semibold text-gray-900 mb-2">Check Your Email</h3>
                  <p className="text-sm text-gray-600">
                    You should receive a confirmation email shortly. Please check your spam folder if you don't see it.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <CardContent className="p-6 text-center">
                  <CheckCircle className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                  <h3 className="font-semibold text-gray-900 mb-2">24-Hour Response</h3>
                  <p className="text-sm text-gray-600">
                    We typically respond to all inquiries within one business day during normal hours.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <CardContent className="p-6 text-center">
                  <Home className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                  <h3 className="font-semibold text-gray-900 mb-2">Explore Our Services</h3>
                  <p className="text-sm text-gray-600">
                    While you wait, browse our comprehensive legal services and resources.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ThankYouPage;
